import type {
  AwardedRequesterContact,
  AwardedSupplierContact,
  OfferComparison,
  OfferInput,
  OfferSupplierView,
  RfqExternalInviteClaimResult,
  RfqExternalInvitePreview,
  RfqRequesterView,
  RfqSupplierListItem,
  RfqSupplierView,
} from '@caterinx/shared-types';
import type { AxiosRequestConfig } from 'axios';

import { api } from './api.js';

/**
 * Der API-Zugang des Portals zu Ausschreibungen und Angeboten.
 *
 * ### Zwei Zugangswege, eine Funktion
 *
 * Dieselbe Seite wird auf zwei Arten geöffnet: angemeldet aus der Liste heraus (Cognito, der
 * Interceptor hängt das Token an) oder über den Magic-Link aus der Einladungsmail (D7). Der
 * Unterschied ist genau **ein Header**, deshalb tragen die Funktionen unten ein optionales
 * Token statt zweier Varianten.
 *
 * **Das Token wandert als `X-Share-Token`-Header, nicht als Query-Parameter.** Ein Token im
 * Query-String steht in Zugriffsprotokollen und im `Referer` jeder Ressource, die die Seite
 * nachlädt. Dass die URL ihn trotzdem enthält, lässt sich nicht vermeiden — die Mail muss
 * ihn ja transportieren; ihn von dort nicht auch noch in jede API-Anfrage zu tragen, schon.
 *
 * Die Typen kommen aus `@caterinx/shared-types`. Wenn im Backend ein Feld dazukommt, bricht
 * hier der Build — statt dass die Oberfläche es stillschweigend nicht anzeigt.
 */

/** Baut die Anfragekonfiguration. Ohne Token: der angemeldete Weg. */
function withToken(token?: string): AxiosRequestConfig {
  return token ? { headers: { 'X-Share-Token': token } } : {};
}

/* --------------------------------------------------------------------------------------------
 * Lieferantenseite
 * ----------------------------------------------------------------------------------------- */

export interface SupplierRfqListResponse {
  count: number;
  items: RfqSupplierListItem[];
}

/** Die Ausschreibungen dieses Betriebs. Nur angemeldet — eine Liste braucht eine Identität. */
export async function fetchMyRfqs(): Promise<SupplierRfqListResponse> {
  return (await api.get<SupplierRfqListResponse>('/rfqs')).data;
}

export interface SupplierRfqResponse {
  rfq: RfqSupplierView;
  /** **Nur das eigene.** Es gibt keinen Weg zu einem fremden Angebot (§5, Punkt 1). */
  offer: OfferSupplierView | null;
  /**
   * Kundenname, Telefonnummer und die exakte Adresse — **nur für den Gewinner, erst nach dem
   * Zuschlag** (D17). Davor und für alle anderen `null`, und zwar serverseitig entschieden.
   */
  requesterContact: AwardedRequesterContact | null;
}

export async function fetchSupplierRfq(rfqId: string, token?: string): Promise<SupplierRfqResponse> {
  return (await api.get<SupplierRfqResponse>(`/rfqs/${rfqId}`, withToken(token))).data;
}

export async function submitOffer(rfqId: string, input: OfferInput, token?: string): Promise<OfferSupplierView> {
  return (await api.put<OfferSupplierView>(`/rfqs/${rfqId}/offer`, input, withToken(token))).data;
}

export async function declineRfq(rfqId: string, reason: string, token?: string): Promise<void> {
  await api.post(`/rfqs/${rfqId}/decline`, { reason }, withToken(token));
}

/**
 * **Zum Menüvorschlag:** Der Upload läuft über `uploadFile('offer_attachment', …)` aus
 * `supplier.ts` — denselben Endpunkt wie Logo und Nachweise, nur mit anderem `kind`. Ein
 * zweiter Upload-Weg wäre eine zweite Stelle, an der jemand die Schlüsselprüfung vergisst.
 *
 * ⚠️ Nur angemeldet: Der Endpunkt hängt hinter dem Cognito-Authorizer. Wer über den
 * Magic-Link kommt, gibt sein Angebot ohne Anhang ab oder meldet sich an. Das ist bewusst so —
 * ein Upload-Endpunkt für Magic-Link-Inhaber wäre ein anonymer Schreibzugang zu S3.
 */

/* --------------------------------------------------------------------------------------------
 * Einladung per E-Mail (D32)
 * ----------------------------------------------------------------------------------------- */

/**
 * Die Vorschau hinter dem Link aus der Einladungsmail — **öffentlich**, ohne Anmeldung. Das
 * Token ist hier Pflicht, nicht optional: Es ist der einzige Ausweis, den ein Betrieb ohne
 * Konto hat. `404` heißt unbekannt oder falsch.
 */
export async function fetchExternalInvite(rfqId: string, token: string): Promise<RfqExternalInvitePreview> {
  return (await api.get<RfqExternalInvitePreview>(`/rfqs/${rfqId}/external-invite`, withToken(token))).data;
}

/**
 * Die Einladung dem angemeldeten Konto zuordnen. Braucht **beides**: das Token aus der Mail und
 * die Anmeldung (der Interceptor hängt das Cognito-Token an). Danach ist die Ausschreibung ganz
 * gewöhnlich eingeladen — die Angebotsmaske läuft ohne Token über die Sitzung.
 */
export async function claimExternalInvite(rfqId: string, token: string): Promise<RfqExternalInviteClaimResult> {
  return (await api.post<RfqExternalInviteClaimResult>(`/rfqs/${rfqId}/external-invite/claim`, {}, withToken(token)))
    .data;
}

/* --------------------------------------------------------------------------------------------
 * Kundenseite
 * ----------------------------------------------------------------------------------------- */

/**
 * Die Kundensicht — nach der **Freigabe** zusätzlich mit den Kontaktdaten des Betriebs.
 *
 * `supplierContact` ist bis dahin `null`, und „bis dahin" heißt seit D24 **nicht mehr** „bis
 * zum Zuschlag": Zwischen beidem steht die Provisionsrechnung an den Betrieb. `handoverReleased`
 * unterscheidet die zwei Zustände, die sonst gleich aussähen — „vergeben, Übergabe läuft" und
 * „vergeben und übergeben".
 *
 * Die Entscheidung fällt serverseitig in `assertRfqAccess`; das Frontend zeigt nur an, was es
 * bekommt, und prüft nichts nach.
 */
export interface RequesterRfqResponse extends RfqRequesterView {
  supplierContact: AwardedSupplierContact | null;
}

export async function fetchRequesterRfq(rfqId: string, token: string): Promise<RequesterRfqResponse> {
  return (await api.get<RequesterRfqResponse>(`/rfqs/${rfqId}`, withToken(token))).data;
}

/**
 * Den Zuschlag erteilen. **Genau einmal möglich** — ein zweiter Aufruf scheitert
 * serverseitig an der Bedingung der Transaktion und antwortet mit 409 `ALREADY_AWARDED`.
 */
export interface AwardResponse {
  rfq: RfqRequesterView;
  /**
   * **Seit D24 immer `null`** direkt nach dem Zuschlag — die Kontaktdaten fließen erst mit
   * der Freigabe durch den Betreiber. Das Feld bleibt im Vertrag, weil die Statusseite an
   * ihm dieselbe Unterscheidung trifft wie bei {@link RequesterRfqResponse}.
   */
  supplierContact: AwardedSupplierContact | null;
}

export async function awardRfq(rfqId: string, supplierId: string, token: string): Promise<AwardResponse> {
  return (await api.post<AwardResponse>(`/rfqs/${rfqId}/award`, { supplierId }, withToken(token))).data;
}

export async function fetchOfferComparison(rfqId: string, token: string): Promise<OfferComparison> {
  return (await api.get<OfferComparison>(`/rfqs/${rfqId}/offers`, withToken(token))).data;
}

/**
 * Die Angebotsphase vorzeitig beenden (D23).
 *
 * Der Kunde darf das **jederzeit**, ohne Mindestzahl an Angeboten und ohne dass alle
 * Eingeladenen geantwortet haben müssen. Die Entscheidung trifft er an den Zahlen auf seiner
 * Statusseite — angeschrieben, eingegangen, noch erwartet —, nicht an einer Sperre.
 *
 * Antwortet mit der aktualisierten Kundensicht: `status` steht danach auf `closed`, und damit
 * schaltet dieselbe Seite den Vergleich samt Zuschlag frei. **409** heißt, dass die Phase
 * bereits beendet war (der Sweeper war schneller) oder noch kein Angebot vorliegt.
 */
export async function closeRfqEarly(rfqId: string, token: string): Promise<RfqRequesterView> {
  return (await api.post<RfqRequesterView>(`/rfqs/${rfqId}/close`, {}, withToken(token))).data;
}

/* --------------------------------------------------------------------------------------------
 * Darstellung
 * ----------------------------------------------------------------------------------------- */

/** `2026-09-12` → `12.09.2026`. */
export function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}.${month}.${year}`;
}

/**
 * Die verbleibende Zeit als Text — **immer mit dem konkreten Zeitpunkt daneben** (D12).
 *
 * Ein Countdown allein ist eine Zahl, die man nachrechnen muss; „noch 3 h 12 min (heute um
 * 18:30 Uhr)" ist ein Termin. Ab einer Stunde Restzeit zählt die Anzeige in Minuten, weil
 * genau dann die Entscheidung fällt, ob man das Angebot noch schreibt.
 */
export function formatRemaining(bidsCloseAt: string, now: number = Date.now()): string {
  const remainingMs = Date.parse(bidsCloseAt) - now;
  if (remainingMs <= 0) return 'abgelaufen';

  const minutes = Math.floor(remainingMs / 60_000);
  if (minutes < 60) return `noch ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `noch ${hours} h ${minutes % 60} min`;

  const days = Math.floor(hours / 24);
  return `noch ${days} ${days === 1 ? 'Tag' : 'Tage'} ${hours % 24} h`;
}

/** Der Fristzeitpunkt in deutscher Schreibweise, in der Zeitzone des Betrachters. */
export function formatDeadline(bidsCloseAt: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(bidsCloseAt));
}
